import axios from 'axios'
import type {
  User,
  Survey,
  Question,
  SurveyResponse,
  AuthResponse,
  CreateSurveyData,
  CreateQuestionData,
  SubmitResponseData,
} from '../types'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear tokens and redirect to login
      localStorage.removeItem('access_token')
      localStorage.removeItem('session_token')
      window.location.href = '/auth/signin'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  register: async (data: { email: string; password: string; full_name?: string }): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', data)
    return response.data
  },

  login: async (data: { email: string; password: string }): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data)
    return response.data
  },

  logout: async (): Promise<{ message: string }> => {
    const response = await api.post('/auth/logout')
    return response.data
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me')
    return response.data
  },

  checkSession: async (): Promise<{ valid: boolean; user_id: string; email: string }> => {
    const response = await api.get('/auth/session')
    return response.data
  },

  // Persist the signed-in user's preferred locale so it follows them across
  // devices. Validated server-side against the enabled-language registry.
  updateLocale: async (locale: string): Promise<{ locale: string }> => {
    const response = await api.patch('/users/me', { locale })
    return response.data
  },
}

// Survey API
export const surveyApi = {
  createSurvey: async (data: CreateSurveyData): Promise<Survey> => {
    const response = await api.post('/surveys', data)
    return response.data
  },

  getSurveys: async (skip = 0, limit = 100): Promise<Survey[]> => {
    const response = await api.get(`/surveys?skip=${skip}&limit=${limit}`)
    return response.data
  },

  getSurvey: async (surveyId: string): Promise<Survey> => {
    const response = await api.get(`/surveys/${surveyId}`)
    return response.data
  },

  getPublicSurvey: async (publicId: string): Promise<Survey> => {
    const response = await api.get(`/surveys/public/${publicId}`)
    return response.data
  },

  updateSurvey: async (
    surveyId: string,
    data: Partial<CreateSurveyData> & { status?: Survey['status']; is_active?: boolean }
  ): Promise<Survey> => {
    const response = await api.put(`/surveys/${surveyId}`, data)
    return response.data
  },

  deleteSurvey: async (surveyId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/surveys/${surveyId}`)
    return response.data
  },

  createQuestion: async (surveyId: string, data: CreateQuestionData): Promise<Question> => {
    const response = await api.post(`/surveys/${surveyId}/questions`, data)
    return response.data
  },

  getSurveyQuestions: async (surveyId: string): Promise<Question[]> => {
    const response = await api.get(`/surveys/${surveyId}/questions`)
    return response.data
  },

  submitResponse: async (surveyId: string, data: SubmitResponseData): Promise<SurveyResponse> => {
    const response = await api.post(`/surveys/${surveyId}/respond`, data)
    return response.data
  },

  getSurveyResponses: async (surveyId: string, skip = 0, limit = 100): Promise<SurveyResponse[]> => {
    const response = await api.get(`/surveys/${surveyId}/responses?skip=${skip}&limit=${limit}`)
    return response.data
  },
}

// Export the api instance for direct use
export { api }

// Utility functions
export const setAuthTokens = (accessToken: string, sessionToken: string) => {
  localStorage.setItem('access_token', accessToken)
  localStorage.setItem('session_token', sessionToken)
}

export const clearAuthTokens = () => {
  localStorage.removeItem('access_token')
  localStorage.removeItem('session_token')
}

export const getAccessToken = () => {
  return localStorage.getItem('access_token')
}

export const isAuthenticated = () => {
  return !!getAccessToken()
}